<template>
  <div v-bind="$attrs" class="flex h-full flex-col">
    <JContainer
      class="relative z-1 border-b border-line px-20 pb-10 md:px-20 md:py-20"
    >
      <div
        v-if="isViewingDetails"
        class="-mt-16 flex items-center justify-center md:mt-0 md:justify-between"
      >
        <button
          class="me-8 p-6 text-header font-black md:text-title"
          @click="openSummary"
        >
          <JIcon name="arrow-left" />
        </button>
        <div class="me-auto text-subject font-bold opacity-80 md:text-title">
          {{ headerTitle }}
        </div>
      </div>

      <div v-else class="flex items-center justify-center md:justify-between">
        <div class="text-subject font-bold opacity-80 md:text-title">
          {{ $t('search.itinerarySummary.title') }}
        </div>
      </div>
    </JContainer>

    <!-- IS LOADING -->
    <CarDetailSkeleton v-if="itinerarySummaryStore.isLoading" />

    <!-- IS EMPTY -->
    <ItinerarySummaryEmpty
      v-if="isEmpty && !itinerarySummaryStore.isLoading && !isViewingDetails"
    />

    <!-- CONTENT -->
    <template
      v-else-if="
        !isEmpty && !itinerarySummaryStore.isLoading && !isViewingDetails
      "
    >
      <div class="flex flex-1 flex-col gap-16 overflow-auto p-20">
        <div
          v-for="(item, index) of Object.entries(
            itinerarySummaryStore.sortedItineraries || {}
          )"
          :key="index"
          :class="{ hidden: !item[1].length }"
        >
          <template v-if="item[0] === 'flight'">
            <ItinerarySummaryFlightCard
              v-for="(flight, i) in item[1]"
              :key="i"
              :item="flight as Flight"
              @show-details="showFlightDetail"
            />
          </template>

          <ItinerarySummaryHotelCard
            v-else-if="item[0] === 'hotel' && item[1].length"
            :items="item[1] as Array<HotelInfoWithRates>"
          />

          <ItinerarySummaryCarCard
            v-else-if="item[0] === 'car' && item[1].length"
            :items="item[1] as Car[]"
          />
        </div>
      </div>

      <div class="p-20">
        <div
          v-if="itinerarySummaryStore.itineraryLength > 1"
          class="mb-[16px] flex flex-row items-center gap-[12px] rounded-[8px] border border-[#FDD28A] bg-[#FFF8EB] px-[12px] py-[10px]"
        >
          <JIcon name="info-outline" size="24" class="text-[#D98B06]" />
          <p class="text-[14px] font-medium leading-[20px] text-[#78510F]">
            {{ $t('search.itinerarySummary.separatedReservation') }}
          </p>
        </div>

        <JButton
          block
          size="lg"
          class="rounded-lg text-pane-header"
          @click="goToBook"
        >
          {{ $t('pay') }}
          {{
            formatCurrency(
              itinerarySummaryStore.getPrice,
              itinerarySummaryStore.getCurrency
            ).value
          }}
          {{ $t('totalPrice') }}
        </JButton>
      </div>
    </template>

    <template v-if="!itinerarySummaryStore.isLoading">
      <ServerError v-if="isError" />

      <FlightDetail
        v-else-if="flightDetailsIsOpen"
        :isLoading="flightInfoIsFetching"
        class="-mt-50 md:mt-0"
        @select="findFlightAndAddToItinerarySummary"
        @delete="onDeselect"
        @retry="flightRefetch"
      />
      <CarDetail
        v-else-if="carDetailsIsOpen"
        :isLoading="carDetailIsFetching"
        @select="onCarSelect"
        @delete="onDeselect"
      />
      <HotelDetail
        v-else-if="hotelDetailsIsOpen"
        showAvailability
        :isLoading="hotelInfoIsFetching"
        @select="handle(), closeHotelDetail()"
        @reserve="closeHotelDetail"
        @vue:unmounted="closeHotelDetail"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue'
import { JButton, JContainer, JIcon } from '@journey/ui-kit'
import { useToggle } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import ItinerarySummaryFlightCard from '../ItinerarySummaryFlightCard/ItinerarySummaryFlightCard.vue'
import ItinerarySummaryHotelCard from '../ItinerarySummaryHotelCard/ItinerarySummaryHotelCard.vue'
import ItinerarySummaryCarCard from '../ItinerarySummaryCarCard/ItinerarySummaryCarCard.vue'
import ItinerarySummaryEmpty from '../ItinerarySummaryEmpty/ItinerarySummaryEmpty.vue'
import type { Emits } from './ItinerarySummaryContent.types'
import { useCurrency, useDevicePlatform } from '@/modules/Core/composables'
import useItinerarySummaryStore from '@/modules/Search/store/itinerarySummary'
import CarDetailSkeleton from '@/modules/Core/components/shared/CarDetail/CarDetailSkeleton/CarDetailSkeleton.vue'
import type { Car, Flight } from '@/modules/Search/types/model/search'
import FlightDetail from '@/modules/Core/components/shared/FlightDetail/FlightDetail.vue'
import {
  useCarInfoQuery,
  useFlightInfoQuery,
  useHotelInfoQuery,
} from '@/modules/Search/requests/queries'
import {
  useItinerary,
  useSetItinerarySummary,
} from '@/modules/Search/composables'
import useFlightSearchResultsStore from '@/modules/Search/store/flightSearchResults'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import ServerError from '@/modules/Core/components/shared/ServerError/ServerError.vue'
import useToastStore from '@/modules/Core/store/toast'
import CarDetail from '@/modules/Core/components/shared/CarDetail/CarDetail.vue'
import useCarSearchResultsStore from '@/modules/Search/store/carSearchResults'
import type { HotelInfoWithRates } from '@/modules/Search/types'
import HotelDetail from '@/modules/Core/components/shared/HotelDetail/HotelDetail.vue'
import useHotelSearchResultsStore from '@/modules/Search/store/hotelSearchResults'
import {
  showCarDetailsInjectionKey,
  showHotelDetailsInjectionKey,
} from '@/modules/Search/constants'
import useHelpStore from '@/modules/Help/store'
import { HELP_SECTIONS } from '@/modules/Core/constants'
import { sleep } from '@/modules/Core/utils'
import featureFlag from '@/config/featureFlag'

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const router = useRouter()
const { isMobile } = useDevicePlatform()

const itinerarySummaryStore = useItinerarySummaryStore()
const { formatCurrency } = useCurrency()
const flightSearchResultsStore = useFlightSearchResultsStore()
const searchPanelHistoryStore = useSearchPanelHistoryStore()
const carSearchResultStore = useCarSearchResultsStore()
const helpStore = useHelpStore()

const { handle } = useSetItinerarySummary()

const [flightDetailsIsOpen, setFlightDetailsIsOpen] = useToggle(false)
const [hotelDetailsIsOpen, setHotelDetailsIsOpen] = useToggle(false)
const [carDetailsIsOpen, setCarDetailsIsOpen] = useToggle(false)

const searchId = computed(() => searchPanelHistoryStore.history.searchId ?? '')
const customerCode = ref(featureFlag.customerCode)
const searchOption = ref(-1)
const { submitItinerary } = useItinerary()
const toastStore = useToastStore()

const isViewingDetails = computed(
  () =>
    flightDetailsIsOpen.value ||
    carDetailsIsOpen.value ||
    hotelDetailsIsOpen.value
)

const headerTitle = computed(() => {
  if (flightDetailsIsOpen.value) {
    return t('search.flightDetails')
  } else if (carDetailsIsOpen.value) {
    return t('search.carDetails')
  } else if (hotelDetailsIsOpen.value) {
    return t('book.hotelDetails')
  }
  return ''
})

const {
  data: flightDetail,
  isFetching: flightInfoIsFetching,
  isError: flightIsError,
  refetch: flightRefetch,
} = useFlightInfoQuery(searchId, searchOption, customerCode, {
  enabled: flightDetailsIsOpen,
})

function showFlightDetail(payload: Flight) {
  searchOption.value = payload.searchOption

  setFlightDetailsIsOpen(true)
}

watch(flightDetail, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  flightSearchResultsStore.setSelectedFlight(value.data)
})

async function findFlightAndAddToItinerarySummary(id: string) {
  const flight = flightSearchResultsStore.findFlightById(id)

  if (!flight) {
    return
  }

  itinerarySummaryStore.select(flight)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  if (!flightDetailsIsOpen.value) {
    return
  }

  setFlightDetailsIsOpen(false)
}

async function onDeselect(uniqueId: string) {
  itinerarySummaryStore.remove(uniqueId)

  const { data } = await submitItinerary()
  setCarDetailsIsOpen(false)
  setFlightDetailsIsOpen(false)
  itinerarySummaryStore.setItineraries(data.data.itinerary)
}

// HOTEL DETAILS
const {
  data: hotelDetail,
  isFetching: hotelInfoIsFetching,
  isError: hotelIsError,
} = useHotelInfoQuery(searchId, searchOption, customerCode, {
  enabled: hotelDetailsIsOpen,
})

const hotelSearchResultStore = useHotelSearchResultsStore()
function clearRooms() {
  hotelSearchResultStore.rooms = []
}

function showHotelDetail(hotel: HotelInfoWithRates) {
  searchOption.value = hotel.searchOption

  setHotelDetailsIsOpen(true)

  if (hotelDetail.value) {
    hotelSearchResultStore.setSelectedHotel(hotelDetail.value.data)
  }
}

function closeHotelDetail() {
  clearRooms()
  setHotelDetailsIsOpen(false)
}

watch(hotelDetail, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  hotelSearchResultStore.setSelectedHotel(value.data)
})

// CAR DETAILS
const provider = ref('')
const pickUpDate = ref('')
const dropOffDate = ref('')
const uniqueId = ref('')
const carCode = ref('')
const supplierCode = ref('')
const searchTerm = ref('')

const {
  data: carDetail,
  isFetching: carDetailIsFetching,
  isError: carIsError,
} = useCarInfoQuery(searchId, searchOption, customerCode, {
  /* FIXME: `refetch` function doesn't work as expected. It returns
   *  `undefined` when called. For now, we enable the query by passing a
   *  `Ref<Boolean>` which behaves correctly.
   */
  enabled: carDetailsIsOpen,
})

function showCarDetail(payload: Car) {
  /* TODO: The provider should be dynamic and filled with the proper name of
   *  the provider returned by the API. For now, the functionality is not
   *  implemented in the API side, so we hard-code the value.
   */
  provider.value = 'FlexibleAutos'
  pickUpDate.value = payload.pickupDateTime
  dropOffDate.value = payload.dropoffDateTime
  carCode.value = payload.code
  uniqueId.value = payload.uniqueId
  supplierCode.value = payload.supplierCode
  searchTerm.value = payload.iataCode
  searchOption.value = payload.searchOption

  setCarDetailsIsOpen(true)
}

watch(carDetail, (value) => {
  if (!value) {
    return
  }

  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  carSearchResultStore.setSelectedCarInfo(value.data)
})

async function onCarSelect(uniqueId: string) {
  const car = carSearchResultStore.findCarByUniqueId(uniqueId)

  if (!car) {
    return
  }

  itinerarySummaryStore.select(car)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  toastStore.addToast({
    title: t('search.itinerarySummary.addMessages.carAdded'),
    color: 'success',
  })

  if (!carDetailsIsOpen.value) {
    return
  }

  setCarDetailsIsOpen(false)
}

function openSummary() {
  setFlightDetailsIsOpen(false)
  setCarDetailsIsOpen(false)
  setHotelDetailsIsOpen(false)
}

const isError = computed(
  () => flightIsError.value || carIsError.value || hotelIsError.value
)

const isEmpty = computed(() => !itinerarySummaryStore.itineraryLength)

provide(showHotelDetailsInjectionKey, { showHotelDetail })
provide(showCarDetailsInjectionKey, { showCarDetail })

helpStore.setRecentActivity(HELP_SECTIONS.ItinerarySummary)

async function goToBook() {
  if (isMobile) {
    emit('close')
    await sleep(100)
  }
  await router.push('/book')
}
</script>
